import React, { useEffect, useMemo, useState } from 'react'
import { createColumnHelper } from '@tanstack/react-table'
import { useNavigate, useParams } from 'react-router'
import { AsyncSelectInput, Button, DateRangePicker, Table } from '@demandlocal/components'
import { useGetCampaignsByAgencyId } from '../../../../api/agencies/useGetCampaignsByAgencyId'
import { Link, useSearchParams } from 'react-router-dom'
import { formatCurrency, formatNumber } from '../../../../lib/utils/formatNumber'
import { DateRange } from '../../../../lib/types/DateRange'
import { useDateRange } from '../../../../store/zustand/dateRangeStore'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { AgencyCampaign } from './AgencyCampaigns'
import { useGetAgencyByAgencyId } from '../../../../api/agencies/useGetAgencyByAgencyId'
import downloadCSV from '../../../../api/downloadCSV'
import toast from 'react-hot-toast'
import { createHtmlPortalNode, OutPortal } from 'react-reverse-portal'
import FiltersContext from '../../../../components/Filters/FiltersContext'
import { useGetAgencyProfileActiveClients } from '../../../../api/agencies/useGetAgencyProfileActiveClients'

const campaignColumnHelper = createColumnHelper<AgencyCampaign>()

export const CampaignsList = (): JSX.Element => {
  const { agency_id } = useParams()
  const { dateRange, setDateRange } = useDateRange()
  const [queryParams] = useSearchParams()
  const { t } = useTranslation('routes')
  const navigate = useNavigate()
  const filtersPortalNode = useMemo(() => createHtmlPortalNode(), [])
  const [downloadingTable, setDownloadingTable] = useState<boolean>(false)
  const { data: agency } = useGetAgencyByAgencyId(agency_id)
  const [tableFilters, setTableFilters] = useState<Record<string, unknown>>({})

  const [filters, setFilters] = useState<DateRange>({
    dateRange: {
      startDate: queryParams.get('startDate') || dateRange.dateRange.startDate,
      endDate: queryParams.get('endDate') || dateRange.dateRange.endDate,
    },
  })

  const onDateRangeChange = (dateRange: DateRange): void => {
    setFilters((prev: DateRange) => ({
      ...prev,
      ...dateRange,
    }))
    setDateRange(dateRange)
  }

  const campaignsColumns = useMemo(
    () => [
      campaignColumnHelper.accessor('dealership_name', {
        header: 'Client',
        cell: ({ getValue, row }) => (
          <Link
            to={t('dealership.profile', {
              dealership: {
                organization_id: agency_id,
                id: row.original.dealership_id,
              },
            })}
            className="text-primary-persian-blue font-bold">
            {getValue()}
          </Link>
        ),
        enableSorting: false,
      }),
      campaignColumnHelper.accessor('client_campaign_display_name', {
        header: 'Campaign',
        cell: ({ getValue, row }) => (
          <Link
            to={t('dealership.campaigns.report', {
              dealership: {
                organization_id: agency_id,
                id: row.original.dealership_id,
              },
              campaign: {
                product_code: row.original.product_code,
                client_campaign_id: row.original.client_campaign_id,
                start_date: row.original.start_date,
                end_date: row.original.end_date,
              },
            })}
            className="text-primary-persian-blue font-bold">
            {getValue()}
          </Link>
        ),
      }),
      campaignColumnHelper.accessor('date', {
        header: 'Month',
        cell: ({ getValue }) => dayjs(getValue()).format('MMM, YYYY'),
        sortingFn: (a, b) =>
          dayjs(a.getValue('date')).isBefore(b.getValue('date')) ? -1 : 1,
      }),
      campaignColumnHelper.accessor('gross_revenue', {
        header: 'Budget',
        cell: ({ getValue }) => formatCurrency(getValue()),
      }),
      campaignColumnHelper.accessor('impressions', {
        header: 'Imps',
        cell: ({ getValue }) => formatNumber(getValue() ?? 0),
      }),
      campaignColumnHelper.accessor('booked_impressions', {
        header: 'Booked Imps',
        cell: ({ getValue }) => formatNumber(getValue() ?? 0),
      }),
      campaignColumnHelper.accessor('clicks', {
        header: 'Clicks',
        cell: ({ getValue }) => formatNumber(getValue() ?? 0),
      }),
      campaignColumnHelper.accessor('views', {
        header: 'Views/Listens',
        cell: ({ getValue }) => formatNumber(getValue() ?? 0),
      }),
      campaignColumnHelper.accessor('ctr', {
        header: 'CTR',
        cell: ({ getValue }) => (getValue() ? `${getValue()}%` : 'N/A'),
      }),
      campaignColumnHelper.accessor('vtr', {
        header: 'VTR',
        cell: ({ getValue }) => (getValue() ? `${getValue()}%` : 'N/A'),
      }),
      campaignColumnHelper.accessor('delivery', {
        header: 'Delivery',
        cell: ({ getValue }) => (getValue() ? `${getValue()}%` : 'N/A'),
      }),
      campaignColumnHelper.accessor('rsv', {
        header: 'Walk Ins',
        cell: ({ getValue }) => formatNumber(getValue() ?? 0),
      }),
    ],
    [agency_id, t],
  )

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-between">
        <div className="rounded-2xl  overflow-hidden">
          <Button
            variant="rainbow"
            onClick={(e) => {
              e.preventDefault()
              navigate(t('agency.profile.campaigns.yearly', { agency_id }))
            }}>
            <div className="flex flex-row gap-3 items-center">
              <span>View Yearly Summary</span>
            </div>
          </Button>
        </div>
        <div className="flex flex-row gap-2 items-center">
          <OutPortal node={filtersPortalNode} />

          <DateRangePicker filters={filters} onChange={onDateRangeChange} />
          <Button
            disabled={downloadingTable}
            variant="primary"
            onClick={async (e) => {
              e.preventDefault()
              const toastId = toast.loading('Downloading...')
              setDownloadingTable(true)
              try {
                const headers = campaignsColumns.map((column) => ({
                  // @ts-ignore
                  accessorKey: column.accessorKey,
                  header: column.header,
                }))
                await downloadCSV({
                  url: `/agencies/${agency_id}/campaigns`,
                  fetchOptions: {
                    start_date: filters.dateRange.startDate,
                    end_date: filters?.dateRange.endDate,
                    agency_id: agency_id,
                  },
                  sortOrder: [
                    {
                      id: 'date',
                      desc: false,
                    },
                  ],
                  headers: headers,
                  fileName: `${agency?.name}_campaigns_${filters.dateRange.startDate}_${filters.dateRange.endDate}.csv`,
                })
                toast.success('Downloaded table!', { id: toastId, duration: 5000 })
                setDownloadingTable(false)
              } catch (err) {
                toast.error('Failed to download table at this time.', {
                  id: toastId,
                  duration: 5000,
                })
                setDownloadingTable(false)
              }
            }}>
            Export CSV
          </Button>
        </div>
      </div>
      <div className="print:hidden">
        <FiltersContext
          portalNode={filtersPortalNode}
          setFilters={setTableFilters}
          defaultValues={tableFilters}>
          <AsyncSelectInput
            label="Clients"
            name={'dealership_id'}
            defaultFetchFnFilters={{
              agency_id,
            }}
            fetchFn={useGetAgencyProfileActiveClients}
            labelKeyName={'dealership'}
            valueKeyName={'id'}
            searchKeyName={'dealership'}
            isClearable
            isMulti
            isSearchable
          />
        </FiltersContext>
      </div>
      <Table
        emptyLabel={'No associated campaigns'}
        columns={campaignsColumns}
        filters={{
          start_date: filters.dateRange.startDate,
          end_date: filters?.dateRange.endDate,
          agency_id: agency_id,
          dealership_id: tableFilters?.dealership_id,
        }}
        fetchFn={useGetCampaignsByAgencyId}
      />
    </div>
  )
}

export default CampaignsList
